import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { filter, firstValueFrom } from 'rxjs';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';
import { RemoteConfigService } from './remote-config.service';
import { ThemeService } from './theme.service';

const ONETRUST_DEFAULT_SCRIPT_ID = '696d0532-ddc5-4c2c-a056-0346168e9c0e';
const ONETRUST_AUTO_BLOCK_URL = 'https://cdn.cookielaw.org/consent';
const ONETRUST_SDK_URL = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
const COOKIE_CONSENT_LAST_SHOWN_KEY = 'cookie_consent_last_shown';

const FREQUENCY_UNIT_DAYS: Record<string, number> = {
  weekly: 7,
  monthly: 30,
  yearly: 365,
};

const JV_ONETRUST_SCRIPT_ID_MAP: Record<string, string> = {
  brphm: 'f0ee5bb5-bad8-4cd4-a15a-1b147eddb4d3',
  hmllc: '8e1e968d-b715-4343-942c-7cfa64a29478',
  hm: '4899e6d8-b48c-4fc9-a7bc-e12dd87198a6',
  lgims: '6fd180de-f679-4746-8ee1-471b0d425a00',
  mscm: '8dd4d053-0ff5-4d56-a598-a44dfab6e7cf',
  mthm: '925439a7-fc53-4ebd-8399-5f693bc70c6f',
  nhcm: '34eddb2e-55c0-427a-82e6-83e5d6e46273',
  ridgeland: '01934f55-3df8-73c8-bcb9-16a27134504d',
  sfl: '019df447-1d98-7b0e-998d-438e74760968',
};

@Injectable({
  providedIn: 'root',
})
export class CookieConsentService {
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);
  private readonly platformService = inject(PlatformService);
  private readonly remoteConfigService = inject(RemoteConfigService);
  private readonly themeService = inject(ThemeService);
  private readonly remoteConfigReady$ = toObservable(this.remoteConfigService.isInitialized);

  async initialize(): Promise<void> {
    if (document.querySelector(`script[src="${ONETRUST_SDK_URL}"]`)) {
      return;
    }

    if (this.platformService.isNativeMobile()) {
      await firstValueFrom(this.remoteConfigReady$.pipe(filter(Boolean)));
      const { cookie_consent } = this.remoteConfigService.config();

      if (!cookie_consent.enabled) {
        this.loggingService.logInfo('CookieConsentService: Cookie consent disabled via remote config');
        return;
      }

      if (cookie_consent.frequency_unit && cookie_consent.frequency_value) {
        if (this.isSuppressedByFrequency(cookie_consent.frequency_unit, cookie_consent.frequency_value)) {
          this.loggingService.logInfo('CookieConsentService: Cookie consent suppressed by frequency gating');
          return;
        }
      }
    }

    const baseScriptId = this.resolveScriptId();
    const domainScriptId = this.env.production ? baseScriptId : `${baseScriptId}-test`;

    const autoBlockScript = document.createElement('script');
    autoBlockScript.async = false;
    autoBlockScript.src = `${ONETRUST_AUTO_BLOCK_URL}/${domainScriptId}/OtAutoBlock.js`;
    document.head.appendChild(autoBlockScript);

    const sdkScript = document.createElement('script');
    sdkScript.async = false;
    sdkScript.src = ONETRUST_SDK_URL;
    sdkScript.setAttribute('data-domain-script', domainScriptId);
    sdkScript.setAttribute('data-document-language', 'true');
    document.head.appendChild(sdkScript);

    const storeTimestamp =
      this.platformService.isNativeMobile() &&
      !!this.remoteConfigService.config().cookie_consent.frequency_unit &&
      !!this.remoteConfigService.config().cookie_consent.frequency_value;

    const wrapperScript = document.createElement('script');
    wrapperScript.textContent = storeTimestamp
      ? [
        'function OptanonWrapper() {',
        '  try { localStorage.setItem("cookie_consent_last_shown", new Date().toISOString()); } catch(e) {}',
        '}',
      ].join('\n')
      : 'function OptanonWrapper() {}';
    document.head.appendChild(wrapperScript);

    this.loggingService.logInfo(`CookieConsentService: Initialized OneTrust with domain script ${domainScriptId}`);
  }

  private isSuppressedByFrequency(unit: string, value: number): boolean {
    try {
      const lastShown = localStorage.getItem(COOKIE_CONSENT_LAST_SHOWN_KEY);
      if (!lastShown) {
        return false;
      }

      const lastShownDate = new Date(lastShown);
      if (isNaN(lastShownDate.getTime())) {
        return false;
      }

      const daysPerUnit = FREQUENCY_UNIT_DAYS[unit] ?? FREQUENCY_UNIT_DAYS['monthly'];
      const intervalMs = daysPerUnit * value * 24 * 60 * 60 * 1000;
      const nextAllowedDate = new Date(lastShownDate.getTime() + intervalMs);

      return new Date() < nextAllowedDate;
    } catch {
      return false;
    }
  }

  private resolveScriptId(): string {
    const currentTheme = this.themeService.getCurrentTheme();
    return JV_ONETRUST_SCRIPT_ID_MAP[currentTheme] ?? ONETRUST_DEFAULT_SCRIPT_ID;
  }
}
